class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;
    try {
      this.predictiveSearchSupported = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}')?.predictiveSearch !== false;
    } catch {
      this.predictiveSearchSupported = true;
    }
    this.resultsId = `${this.input.id}-predictive-search-results`;
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.resizeResults = () => { if (this.isOpen) this.updateResultsHeight(); };

    this.setupEventListeners();
  }

  connectedCallback() {
    window.addEventListener('resize', this.resizeResults);
    window.visualViewport?.addEventListener('resize', this.resizeResults);
    window.visualViewport?.addEventListener('scroll', this.resizeResults);
  }

  disconnectedCallback() {
    this.close();
    window.removeEventListener('resize', this.resizeResults);
    window.visualViewport?.removeEventListener('resize', this.resizeResults);
    window.visualViewport?.removeEventListener('scroll', this.resizeResults);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', () => {
      this.abortController.abort();
      this.requestId += 1;
      this.querySelectorAll('[aria-selected="true"]').forEach(option => option.setAttribute('aria-selected', 'false'));
      this.input.removeAttribute('aria-activedescendant');
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
      this.removeAttribute('results');
      this.updateSearchForTerm(this.searchTerm, this.getQuery());
      if (!this.getQuery()) this.close(true);
    }, true);
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.requestId += 1;
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;
    this.cancelPendingChange();

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.isComposing) return;
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        if (!this.isOpen && !this.hasAttribute('loading')) this.onFocus();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        if (!this.isOpen && !this.hasAttribute('loading')) this.onFocus();
        this.switchOption('down');
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;
    const selectedOption = this.querySelector('[aria-selected="true"]');
    if (event.code === 'Enter' && this.isOpen && selectedOption && selectedOption.offsetParent !== null) {
      event.preventDefault();
      this.selectOption();
      return;
    }
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (searchForTextElement && this.dataset.searchForLabel) {
      searchForTextElement.textContent = this.dataset.searchForLabel.replace('[query]', newTerm);
      return;
    }
    const currentButtonText = searchForTextElement?.textContent;
    if (currentButtonText) {
      if (!previousTerm || currentButtonText.split(previousTerm).length !== 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.textContent = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    const activeElementIndex = selectedElementIndex === -1
      ? (moveUp ? allVisibleElements.length - 1 : 0)
      : (selectedElementIndex + (moveUp ? -1 : 1) + allVisibleElements.length) % allVisibleElements.length;

    this.statusElement.textContent = '';

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
    activeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const requestId = ++this.requestId;
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (!this.predictiveSearchSupported) {
      this.renderSearchFallback(searchTerm);
      return;
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,query',
      'resources[options][fields]': 'title,product_type,variants.title,vendor',
      'resources[options][unavailable_products]': 'show',
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML;
        if (!resultsMarkup) throw new Error('Missing search results');
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          if (predictiveSearchInstance.cachedResults) predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (requestId !== this.requestId || this.getQuery() !== searchTerm) {
          searchDeferred?.reject(new DOMException('Replaced query', 'AbortError'));
          return;
        }
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        searchDeferred?.reject(error);
        if (error?.name === 'AbortError' || error?.code === 20 || requestId !== this.requestId) return;
        if (this.getQuery() === searchTerm) this.renderSearchFallback(searchTerm);
      });
  }

  renderSearchFallback(searchTerm) {
    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'predictive-search__item predictive-search__search-for-button link link--text';
    button.textContent = (this.dataset.searchForLabel || this.input.placeholder || 'Search').replace('[query]', searchTerm);
    button.id = `${this.resultsId}-submit`;
    button.setAttribute('role', 'option');
    button.setAttribute('aria-selected', 'false');
    button.setAttribute('data-predictive-search-search-for-text', '');
    const container = document.createElement('div');
    container.id = this.resultsId;
    container.setAttribute('role', 'listbox');
    container.append(button);
    this.predictiveSearchResults.replaceChildren(container);
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.input.removeAttribute('aria-activedescendant');
    this.setLiveRegionText(button.textContent);
    if (this.contains(document.activeElement)) this.open();
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    clearTimeout(this.liveRegionTimeout);
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    this.liveRegionTimeout = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.input.removeAttribute('aria-activedescendant');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      const previous = element.id;
      element.id = `${this.input.id}-${previous}`;
      ids.set(previous, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('button.predictive-search__item').forEach((button, index) => {
      if (!button.id) button.id = `${this.resultsId}-action-${index}`;
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-controls], [aria-describedby]').forEach(element => {
      ['aria-labelledby', 'aria-controls', 'aria-describedby'].forEach(attribute => {
        if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
      });
    });
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    if (this.contains(document.activeElement)) this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    return Math.max(0, bottom - this.predictiveSearchResults.getBoundingClientRect().top - 16);
  }

  updateResultsHeight() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  open() {
    this.setAttribute('open', true);
    this.updateResultsHeight();
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.cancelPendingChange();
    this.abortController.abort();
    this.requestId += 1;
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
